import React, {memo, useCallback, useEffect} from "react";
import Picture from "../common/Picture";
import {MenuData, SubLink} from "../../config/menuData";
import {CloseIcon, EnquireIcon} from "../common/Icons";
import {Link} from "react-router-dom";

const SubMenuLink: React.FC<{
    link: SubLink;
    onClick: () => void;
    index: number;
}> = memo(({link, onClick, index}) => {
    const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.currentTarget;
        target.onerror = null;
        target.src = 'https://placehold.co/600x200/4B4235/F6F4E9?text=Image';
    }, []);

    return (
        <Link
            to={`/${link.targetId}`}
            onClick={onClick}
            className="relative block w-full overflow-hidden rounded-md group focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50"
            style={{
                animationDelay: `${index * 100}s`,
            }}
        >
            <div className="relative h-16 lg:h-24 overflow-hidden">
                <Picture
                    {...(link.previewImage.toLowerCase().endsWith('.webp') ? { webpSrc: link.previewImage } : link.previewImage.toLowerCase().endsWith('.png') ? { pngSrc: link.previewImage } : { jpgSrc: link.previewImage })}
                    alt={link.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-110"
                    onErrorSrc={'https://placehold.co/600x200/4B4235/F6F4E9?text=Image'}
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent"/>

                {/* Sliding Overlay Effect */}
                <div
                    className="absolute inset-0 bg-ivory transform transition-transform duration-300 ease-in-out group-hover:translate-x-full"/>
            </div>

            {/* Content Overlay */}
            <div className="absolute inset-0 flex items-center p-4 sm:p-6 lg:p-8">
                <div className="relative z-10">
                    <h3 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl heading-font text-royal-pine group-hover:text-ivory transition-colors duration-300 leading-tight">
                        {link.name}
                    </h3>
                    <div
                        className="w-0 group-hover:w-full h-0.5 bg-logo-color mt-2 transition-all duration-300 delay-150"/>
                </div>
            </div>
        </Link>
    )
});

SubMenuLink.displayName = "SubMenuLink";

interface FullScreenMenuProps {
    isMenuOpen: boolean;
    onMenuClose: () => void;
    onEnquireClick: () => void;
    menuData: MenuData;
}

const FullScreenMenu: React.FC<FullScreenMenuProps> = ({
                                                                isMenuOpen,
                                                                onMenuClose,
                                                                onEnquireClick,
                                                                menuData
                                                            }) => {
    const clubExperienceItem = menuData['THE CLUB EXPERIENCE'];

    const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.currentTarget;
        target.onerror = null;
        target.src = 'https://placehold.co/150x60/F6F4E9/153D40?text=All+Aces';
    }, []);

    const handleEnquireClick = useCallback(() => {
        onMenuClose();
        onEnquireClick();
    }, [onMenuClose, onEnquireClick]);

    useEffect(() => {
        if (isMenuOpen) {
            const closeButton = document.querySelector('[data-menu-close]') as HTMLElement;
            if (closeButton) {
                closeButton.focus();
            }
        }
    }, [isMenuOpen]);

    return (
        <div
            className={`fixed inset-0 bg-ivory z-50 transition-transform duration-500 ease-in-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div
                className="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col overflow-hidden gap-4 sm:gap-6 lg:gap-8">
                {/* Header */}
                <div className="flex items-center justify-between h-16 text-royal-pine">
                    <Link
                        to="/"
                        onClick={onMenuClose}
                        className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 rounded-md"
                    >
                        <Picture
                            pngSrc="/images/logo.png"
                            alt="All Aces Club Logo"
                            className="h-12 w-auto cursor-pointer"
                            loading="eager"
                            onErrorSrc={'https://placehold.co/150x60/F6F4E9/153D40?text=All+Aces'}
                        />
                    </Link>
                    <button
                        data-menu-close
                        onClick={onMenuClose}
                        className="p-2 -m-2 hover:bg-royal-pine hover:bg-opacity-10 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50"
                        aria-label="Close Menu"
                    >
                        <CloseIcon className="h-6 w-6 sm:w-8 sm:h-8 hover:text-logo-color transition-colors"/>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="space-y-4">
                        {/* Main Navigation */}
                        <nav
                            role="navigation"
                            aria-label="Main Navigation"
                            className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center lg:justify-between border-b border-muted-sage border-opacity-30 gap-4 pb-4"
                        >
                            <div className="flex flex-col lg:flex-row gap-y-4 lg:gap-y-0 lg:gap-x-8">
                                {Object.keys(menuData).map(title => {
                                    const item = menuData[title];

                                    if (item.subLinks.length > 0) {
                                        return (
                                            <div key={title} className="flex-shrink-0">
                                                <span
                                                    className="nav-font text-lg sm:text-xl tracking-wider text-logo-color font-medium"
                                                >
                                                    {title}
                                                </span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <Link
                                            key={title}
                                            to={`/${item.targetId}`}
                                            onClick={onMenuClose}
                                            className="nav-font text-lg sm:text-xl tracking-wider transition-colors text-royal-pine hover:text-logo-color focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 rounded-md"
                                        >
                                            {title}
                                        </Link>
                                    );
                                })}
                            </div>
                            <button
                                onClick={handleEnquireClick}
                                className="mt-4 md:mt-0 w-full md:w-auto flex items-center justify-center gap-2 border text-royal-pine border-royal-pine px-6 py-3 rounded-md font-semibold hover:bg-royal-pine hover:text-ivory transition-all">
                                <EnquireIcon className="h-5 w-5"/>
                                ENQUIRE NOW
                            </button>
                        </nav>
                        <div className="my-0">
                            <h3 className="sr-only">Club Experience Options</h3>
                            <ul className="space-y-4">
                                {clubExperienceItem.subLinks.map((link, index) => (
                                    <li key={link.name}>
                                        <SubMenuLink
                                            link={link}
                                            onClick={onMenuClose}
                                            index={index}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FullScreenMenu;